<script setup lang="ts">
import {
  usePageConnectorStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import {
  isAlternativeTicketSuggestionActivitySegment,
  isAvailableActivitySegment,
  isUnavailableActivitySegment,
} from '../../../models';
import type { ItineraryActivitySegment } from '../../../models';
import {
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../../stores';

import AvailableActivitySegment from './AvailableActivitySegment.vue';
import UnavailableActivitySegment from './UnavailableActivitySegment.vue';
import AlternativeTicketSuggestionActivitySegment from './AlternativeTicketSuggestionActivitySegment.vue';

// NOTE: Important:
// * this component is smart
// * keep child components of this one dumb
// * every action in this component must be guarded by print mode flag

interface ItineraryActivitySegmentProps {
  segment: ItineraryActivitySegment;
  printMode: boolean;
}

const props = defineProps<ItineraryActivitySegmentProps>();

const pageConnectorStore = usePageConnectorStore();
const routerStore = useRouterStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const statsigEventsStore = useStatsigEventsStore();

function onChangeProduct(): void {
  if (props.printMode) {
    return;
  }

  routerStore.navigateToActivitySelection(
    props.segment.context.ingredientIndex
  );
}

function onEditFilters(): void {
  if (props.printMode) {
    return;
  }

  pageConnectorStore.openActivitySelectionFilters(
    props.segment.context.ingredientIndex
  );
}

function onRemoveActivity(): void {
  if (props.printMode) {
    return;
  }

  statsigEventsStore.logTripOverviewActivityRemoved();
  tripOverviewPageStore.deleteIngredientById(
    props.segment.context.ingredientId
  );
}

function onShowDetails(): void {
  if (props.printMode || !props.segment.productCardData) {
    return;
  }

  tripOverviewPanelsStore.openActivityDetailsPanel(
    props.segment.context.hotelCode,
    props.segment.context.ingredientId
  );
}

function onShowTickets(): void {
  if (props.printMode) {
    return;
  }

  if (!props.segment.productCardData) {
    throw new Error(
      'Expected productCardData when showing product card segment!'
    );
  }

  statsigEventsStore.logTripOverviewSeeAlternativeTicketsWhenNoAvail();

  if (props.segment.context.hotelCode) {
    tripOverviewPanelsStore.openActivityDetailsPanel(
      props.segment.context.hotelCode,
      props.segment.context.ingredientId,
      true
    );
  }
}

function confirmAlternative(): void {
  if (isAlternativeTicketSuggestionActivitySegment(props.segment)) {
    tripOverviewPageStore.confirmAlternativeTicketSelection(props.segment);
  }
}

function onTryAnotherDate(): void {
  if (props.printMode) {
    return;
  }

  tripOverviewPageStore.scrollToCalendar();
}
</script>

<template>
  <AvailableActivitySegment
    v-if="isAvailableActivitySegment(props.segment)"
    :segment="props.segment"
    :print-mode="props.printMode"
    @change-product="onChangeProduct"
    @remove-activity="onRemoveActivity"
    @show-details="onShowDetails"
  />

  <AlternativeTicketSuggestionActivitySegment
    v-if="isAlternativeTicketSuggestionActivitySegment(props.segment)"
    :segment="props.segment"
    @change-product="onChangeProduct"
    @remove-activity="onRemoveActivity"
    @show-details="onShowDetails"
    @show-tickets="onShowTickets"
    @confirm-alternative="confirmAlternative"
  />

  <UnavailableActivitySegment
    v-if="isUnavailableActivitySegment(props.segment)"
    :segment="props.segment"
    :print-mode="props.printMode"
    :unavailability-message="
      tripOverviewPageStore.getUnavailabilityMessage(
        props.segment.segmentType,
        props.segment.reasons
      )
    "
    @edit-filters="onEditFilters"
    @remove-activity="onRemoveActivity"
    @try-another-date="onTryAnotherDate"
  />
</template>
